import React, { ForwardedRef, ReactElement, forwardRef } from "react";
import { cx } from "@emotion/css";
import isEmpty from "lodash/isEmpty";
import { useEnigmaToken } from "@hooks/useEnigmaToken";
import { Loader } from "../loader/loader";
import { Pagination } from "../pagination/pagination";
import TableCore from "./core/components/tableCore";
import TableEmptyState from "./core/components/tableEmptyState";
import TableLoader from "./core/components/tableLoader";
import {
    StyledTableShadowContainer,
    StyledTableWrapper,
    mainContainer,
    paginator,
} from "./table.styles";
import { ITableProps } from "./table.types";
import { useTable } from "./useTable";

/**
 * @description Table component
 *
 * @reference https://tanstack.com/table/v8/docs/guide/introduction
 *
 * @returns {React.ReactElement}
 */

const Table = forwardRef(
    <T,>(
        {
            data = [],
            columns = [],
            rowUniqueKey,
            hideColumns = [],
            pagination,
            onPageChange,
            onRowClick,
            rowHeight,
            rowClassName,
            wrapperClassName,
            isLoading = false,
            loader = <Loader size="medium" tip="" />,
            scrollable = true,
            emptyView = {},
            columnClassName = "",
            paginationClassName,
            height,
            stickyHeader,
            onSort,
            selectionMode,
            onSelectionChange,
            getCheckBoxState,
            ...nativeTableProps
        }: ITableProps<T>,
        ref: ForwardedRef<HTMLTableElement>
    ) => {
        const {
            table,
            shouldPaginate,
            pageNumber,
            pageSize,
            totalRows,
            showScrollShadow,
            tableContainer,
            handlePageChange,
            handleSort,
            sortingState,
        } = useTable<T>({
            data,
            columns,
            hideColumns,
            pagination,
            onPageChange,
            onSort,
            selectionMode,
            onSelectionChange,
            rowUniqueKey,
            getCheckBoxState,
        });

        const token = useEnigmaToken();

        return (
            <div className={mainContainer}>
                <StyledTableShadowContainer
                    withShadow={scrollable && showScrollShadow}
                >
                    <StyledTableWrapper
                        token={token}
                        ref={tableContainer}
                        isLoading={isLoading}
                        isScrollable={scrollable}
                        className={wrapperClassName}
                        height={height}
                    >
                        {!isEmpty(data) ? (
                            <TableCore<T>
                                onRowClick={onRowClick}
                                pageSize={pageSize}
                                rowClassName={rowClassName}
                                ref={ref}
                                rowHeight={rowHeight}
                                rowUniqueKey={rowUniqueKey}
                                table={table}
                                isLoading={isLoading}
                                columnClassName={columnClassName}
                                scrollable={scrollable}
                                stickyHeader={stickyHeader}
                                handleSort={handleSort}
                                sortingState={sortingState}
                                {...nativeTableProps}
                            />
                        ) : null}
                        {isEmpty(data) && !isLoading ? (
                            <TableEmptyState<T> emptyView={emptyView} />
                        ) : null}
                        {isLoading ? (
                            <TableLoader> {loader} </TableLoader>
                        ) : null}
                    </StyledTableWrapper>
                </StyledTableShadowContainer>
                {shouldPaginate && (
                    <Pagination
                        className={cx(paginator, paginationClassName)}
                        current={pageNumber}
                        total={totalRows}
                        pageSize={pageSize}
                        onChange={handlePageChange}
                        onShowSizeChange={handlePageChange}
                        showSizeChanger={Boolean(pagination?.showSizeChanger)}
                    />
                )}
            </div>
        );
    }
);

Table.displayName = "Table";

export default Table as <T>(
    props: ITableProps<T> & { ref?: ForwardedRef<HTMLTableElement> }
) => ReactElement;
